/**
 * Pure helpers over one published listing (the real `storefront` payload row).
 * No I/O, no React: safe to unit-test and to reuse from fixtures.
 */

import { specLine as typeSpecLine, formatValue, viewRows, customRows } from '@/lib/productTypes';
import { attrsOf } from './shop';

export const GRADE_VARIANT = { A: 'success', B: 'primary', C: 'warning' };

export const GRADE_LABEL = {
  A: 'Grade A · Excellent',
  B: 'Grade B · Very good',
  C: 'Grade C · Good',
};

/** The short grade label on a card: "Grade A", or "Not graded" when no grade is set. */
export function gradeLabel(grade) {
  return grade ? `Grade ${grade}` : 'Not graded';
}

/** The short spec line of a listing (client/src/lib/productTypes specLine, for its attributes). */
export function specLine(l, type) {
  return l ? typeSpecLine(type, attrsOf(l)) : '';
}

/** The product page's specification rows: the type's `views.specs`, else every attribute with a value. */
export function specTable(l, type) {
  if (!l || !type) return [];
  const view = viewRows(type, 'specs', attrsOf(l));
  const platform = view ? view.map((e) => ({ label: e.label, value: e.value })) : specRows(l, type).map((r) => ({ label: r.label, value: r.value }));
  // Then the store's own fields (ADR 0009 §4, MERCH-86), labelled, in the store's order; deleted ones never.
  return [...platform, ...customRows(type, attrsOf(l)).map((r) => ({ label: r.label, value: r.value }))];
}

export function modelName(l) {
  return [l.brand, l.series, l.model_name].filter(Boolean).join(' ');
}

/** Whole-number discount percentage vs MRP, or null when there is no MRP above price. */
export function discountPct(l) {
  if (!l || !l.mrp_paise || !l.price_paise || l.mrp_paise <= l.price_paise) return null;
  return Math.round(((l.mrp_paise - l.price_paise) / l.mrp_paise) * 100);
}

/** [{ label, value }] for every attribute of the listing's type that has a value (product page specs). */
export function specRows(l, type) {
  if (!type) return [];
  const a = attrsOf(l);
  return type.attributes.map((x) => ({ key: x.key, label: x.label, value: formatValue(x, a[x.key] ?? l[x.key]) })).filter((r) => r.value);
}

/**
 * The condition badge of a listing, cart or order line (D98): the server's badge from the
 * condition registry (lib/catalog/conditions, sent on every payload). Without one (an old
 * cached payload) the key is shown as words.
 */
export function conditionBadge(l) {
  if (!l) return null;
  if (l.condition_badge || l.condition_label) return l.condition_badge || l.condition_label;
  const words = l.condition ? String(l.condition).replace(/_/g, ' ') : '';
  return words ? words[0].toUpperCase() + words.slice(1) : null;
}

/** Does this item's condition carry a grade? Rows from before the registry (no flag) always did. */
export function gradeApplies(l) {
  return Boolean(l) && l.grade_applies !== false;
}

/** Does this item's condition disclose battery health at all? Rows from before the registry did. */
export function batteryApplies(l) {
  return Boolean(l) && l.battery_applies !== false;
}

/** Does this item's condition disclose battery health, with a reading to show? */
export function batteryShown(l) {
  return batteryApplies(l) && l.battery_health_pct != null;
}

/** Does this item's condition carry the inspection gate? Rows from before the registry did. */
export function inspectionApplies(l) {
  return Boolean(l) && l.inspection_applies !== false;
}

